// stockfish.js - Utility for stockfish engine integration

// Track the Stockfish engine instance
let stockfishInstance = null;
let stockfishReady = false;
let messageListeners = [];

// Cache for storing analysis results
let analysisCache = {
    cachedFen: null,        // The FEN position that was analyzed
    cachedResults: null,    // The full analysis results
    cachedLines: null,      // Processed PV lines with SAN moves
    followingLineIndex: -1, // Which line (0, 1, 2) we're currently following
    movesMade: 0,           // Number of moves made following a cached line
    lastMoveTimestamp: 0    // When the last move was made
};

// Default recalculation threshold (configurable)
let recalculationThreshold = 6;

/**
 * Set the recalculation threshold
 * @param {number} threshold - Number of moves to follow before recalculating (1-10)
 */
export const setRecalculationThreshold = (threshold) => {
    const value = parseInt(threshold);
    if (!isNaN(value) && value >= 1 && value <= 10) {
        recalculationThreshold = value;
        // Save to localStorage for persistence
        try {
            localStorage.setItem('stockfishRecalculationThreshold', value.toString());
        } catch (e) {
            console.warn('Could not save recalculation threshold to localStorage', e);
        }
    }
};

/**
 * Get the current recalculation threshold
 * @returns {number} Current threshold value
 */
export const getRecalculationThreshold = () => {
    return recalculationThreshold;
};

/**
 * Load saved recalculation threshold from localStorage if available
 */
export const loadRecalculationThreshold = () => {
    try {
        const savedThreshold = localStorage.getItem('stockfishRecalculationThreshold');
        if (savedThreshold) {
            const value = parseInt(savedThreshold);
            if (!isNaN(value) && value >= 1 && value <= 10) {
                recalculationThreshold = value;
            }
        }
    } catch (e) {
        console.warn('Could not load recalculation threshold from localStorage', e);
    }
};

// Initialize threshold from localStorage
loadRecalculationThreshold();

/**
 * Initialize Stockfish engine if not already initialized
 * @returns {Promise} Promise that resolves when Stockfish is ready
 */
export const initStockfish = async () => {
    // Return existing instance if already initialized
    if (stockfishInstance && stockfishReady) {
        return stockfishInstance;
    }

    // Create a new stockfish instance
    try {

        // Use a web worker to run Stockfish in the background
        const workerUrl = `${process.env.PUBLIC_URL}/stockfish-nnue-16-single.js`;
        stockfishInstance = new Worker(workerUrl);

        // Set up the message handler
        stockfishInstance.onmessage = (event) => {
            const message = event.data;

            // Notify all registered listeners
            messageListeners.forEach(listener => listener(message));
        };

        // Wait for the engine to be ready
        await new Promise((resolve) => {
            const readyListener = (message) => {
                if (message === 'readyok') {
                    messageListeners = messageListeners.filter(l => l !== readyListener);
                    resolve();
                }
            };

            messageListeners.push(readyListener);

            // Initialize UCI mode
            stockfishInstance.postMessage('uci');
            stockfishInstance.postMessage('isready');
        });

        // Set options
        stockfishInstance.postMessage('setoption name Threads value 1');
        stockfishInstance.postMessage('setoption name Hash value 32');
        stockfishInstance.postMessage('setoption name MultiPV value 3');

        // Mark as ready
        stockfishReady = true;

        return stockfishInstance;
    } catch (error) {
        console.error('Failed to initialize Stockfish:', error);
        throw error;
    }
};

/**
 * Register a listener for Stockfish messages
 * @param {Function} listener - Function to call with Stockfish messages
 * @returns {Function} Function to remove the listener
 */
export const addStockfishListener = (listener) => {
    messageListeners.push(listener);
    return () => {
        messageListeners = messageListeners.filter(l => l !== listener);
    };
};

/**
 * Evaluate a chess position with Stockfish
 * @param {string} fen - FEN string of the position
 * @param {Object} options - Options for analysis
 * @param {number} options.depth - Analysis depth (default: 15)
 * @param {number} options.multipv - Number of principal variations (default: 3)
 * @param {boolean} options.forceCalculation - Force calculation even if cached result exists
 * @returns {Promise} Promise that resolves with stockfish's evaluation
 */
export const evaluatePosition = async (fen, options = {}) => {
    try {
        const depth = options.depth || 15;
        const multipv = options.multipv || 3;
        const forceCalculation = options.forceCalculation || false;

        // Reset move counter if forcing recalculation
        if (forceCalculation) {
            analysisCache.movesMade = 0;
            analysisCache.followingLineIndex = -1;
        }

        // Check cache first (if not forcing recalculation)
        if (!forceCalculation && analysisCache.cachedFen === fen && analysisCache.cachedResults) {
            return analysisCache.cachedResults;
        }

        // Initialize Stockfish if not already initialized
        if (!stockfishInstance || !stockfishReady) {
            await initStockfish();
        }

        // Determine who's turn it is from the FEN string
        // The side to move is the 2nd field (after the piece positions)
        const fenParts = fen.split(' ');
        const sideToMove = fenParts[1]; // 'w' for White, 'b' for Black
        const isBlackToMove = sideToMove === 'b';

        return new Promise((resolve) => {
            const results = [];
            const infoRegex = /info depth (\d+) seldepth \d+ multipv (\d+) score (cp|mate) ([-\d]+).+pv (.+)/;
            const bestmoveRegex = /bestmove ([a-h][1-8][a-h][1-8][qbnr]?)/;

            // Create a one-time listener for this evaluation
            const removeListener = addStockfishListener((message) => {
                // Check for score info
                const infoMatch = message.match(infoRegex);
                if (infoMatch && parseInt(infoMatch[1]) === depth) {
                    const pvNumber = parseInt(infoMatch[2]);
                    const scoreType = infoMatch[3];
                    let scoreValue = parseInt(infoMatch[4]);
                    const pv = infoMatch[5].split(' ');

                    // If it's Black's turn, we need to negate the score to get White's perspective
                    if (isBlackToMove) {
                        scoreValue = -scoreValue;
                    }

                    // Format the evaluation
                    let evaluation;
                    if (scoreType === 'cp') {
                        evaluation = scoreValue / 100; // Convert centipawns to pawns
                    } else {
                        // Mate score - show as M5 (mate in 5 for White) or M-5 (mate in 5 for Black)
                        evaluation = scoreValue > 0 ? `M${Math.abs(scoreValue)}` : `M-${Math.abs(scoreValue)}`;
                    }

                    results[pvNumber - 1] = {
                        multipv: pvNumber,
                        depth,
                        score: {
                            type: scoreType,
                            value: scoreValue
                        },
                        evaluation,
                        pv,
                        bestMove: pv[0]
                    };
                }

                // Check for bestmove
                const bestmoveMatch = message.match(bestmoveRegex);
                if (bestmoveMatch) {
                    // Remove this listener since we're done
                    removeListener();

                    // Create the analysis result
                    const analysisResult = {
                        fen,
                        results,
                        bestMove: bestmoveMatch[1]
                    };

                    // Cache the result
                    analysisCache.cachedFen = fen;
                    analysisCache.cachedResults = analysisResult;
                    analysisCache.movesMade = 0;
                    analysisCache.followingLineIndex = -1;
                    analysisCache.lastMoveTimestamp = Date.now();

                    // Resolve with the evaluation results
                    resolve(analysisResult);
                }
            });

            // Send position and go command
            stockfishInstance.postMessage(`position fen ${fen}`);
            stockfishInstance.postMessage(`setoption name MultiPV value ${multipv}`);
            stockfishInstance.postMessage(`go depth ${depth}`);
        });
    } catch (error) {
        console.error("Error in evaluatePosition:", error);
        throw error;
    }
};

/**
 * Store processed PV lines for caching
 * @param {Array} processedLines - Array of processed principal variation lines
 */
export const storeCachedLines = (processedLines) => {
    if (Array.isArray(processedLines) && processedLines.length > 0) {
        analysisCache.cachedLines = processedLines;
        // Reset move counter when storing new lines
        analysisCache.movesMade = 0;
        analysisCache.followingLineIndex = -1;
    }
};

/**
 * Check if a move matches any of the cached PV lines
 * @param {string} move - The SAN formatted move to check
 * @returns {Object} Result object containing match info or null if no match
 */
export const checkMoveAgainstCache = (move) => {
    if (!analysisCache.cachedLines || analysisCache.cachedLines.length === 0) {
        return null;
    }

    // Handle the case where we're already following a line
    if (analysisCache.followingLineIndex >= 0) {
        const line = analysisCache.cachedLines[analysisCache.followingLineIndex];

        // Check if this move matches the next expected move in the line we're following
        if (line && line.moves && line.moves[analysisCache.movesMade] === move) {
            // Move matches the next expected move in the line we're following
            analysisCache.movesMade++;
            analysisCache.lastMoveTimestamp = Date.now();

            const needsRecalculation = analysisCache.movesMade >= recalculationThreshold;

            return {
                lineIndex: analysisCache.followingLineIndex,
                movesMade: analysisCache.movesMade,
                needsRecalculation: needsRecalculation,
                expectedNextMove: line.moves[analysisCache.movesMade] || null,
                evaluation: line.evaluation,
                isCachedLine: true
            };
        } else {
            // Diverged from the line we were following
            return {
                lineIndex: -1,
                diverged: true,
                needsRecalculation: true
            };
        }
    }

    // Check if this move matches the first move of any cached line
    for (let i = 0; i < analysisCache.cachedLines.length; i++) {
        const line = analysisCache.cachedLines[i];
        if (line && line.moves && line.moves[0] === move) {
            // Found a match! This move matches the first move of a cached line
            analysisCache.followingLineIndex = i;
            analysisCache.movesMade = 1;
            analysisCache.lastMoveTimestamp = Date.now();

            return {
                lineIndex: i,
                movesMade: 1,
                needsRecalculation: false,
                expectedNextMove: line.moves[1] || null,
                evaluation: line.evaluation,
                isCachedLine: true
            };
        }
    }

    // No match found in any cached line
    return {
        lineIndex: -1,
        needsRecalculation: true
    };
};

/**
 * Get the current cache status
 * @returns {Object} Current cache status
 */
export const getCacheStatus = () => {
    return {
        isFollowingCachedLine: analysisCache.followingLineIndex >= 0,
        lineIndex: analysisCache.followingLineIndex,
        movesMade: analysisCache.movesMade,
        threshold: recalculationThreshold,
        cachedLines: analysisCache.cachedLines || [],
        movesUntilRecalculation: Math.max(0, recalculationThreshold - analysisCache.movesMade),
        recalculationThreshold
    };
};

/**
 * Reset the cache when analysis is manually stopped
 */
export const resetCache = () => {
    analysisCache = {
        cachedFen: null,
        cachedResults: null,
        cachedLines: null,
        followingLineIndex: -1,
        movesMade: 0,
        lastMoveTimestamp: 0
    };
};

/**
 * Stop any ongoing Stockfish analysis
 */
export const stopAnalysis = () => {
    if (stockfishInstance) {
        stockfishInstance.postMessage('stop');
    }
};

/**
 * Clean up Stockfish resources
 */
export const cleanupStockfish = () => {
    if (stockfishInstance) {
        stockfishInstance.postMessage('quit');
        stockfishInstance.terminate();
        stockfishInstance = null;
        stockfishReady = false;
        messageListeners = [];
    }

    // Also reset the cache when cleaning up
    resetCache();
}; 